import type {
  SupplierStorefrontV2GetResponseType,
  SupplierGetResponseType,
} from "@sellar-io/api-v2/types";
import { useUnifiedCheckoutBasketList } from "~/hooks/api/v2/unified-checkout-basket/useUnifiedCheckoutBaskets";
import {
  usePriceTierOrderRequirements,
  type OrderRequirementsBasket,
} from "~/hooks/usePriceTierOrderRequirements";
import { useUnifiedCheckoutDraftOrders } from "~/pages/unifiedCheckout/hooks/useUnifiedCheckoutDraftOrders";
import { calculateUnifiedCheckoutOrderPricing } from "~/pages/unifiedCheckout/lib/calculateUnifiedCheckoutOrderPricing";
import { isPromotionalOrderItem } from "~/pages/stockList/categoryPage/Storefront/StorefrontPromotion/isPromotionalOrderItem";

export const useProductDialogOrderRequirements = ({
  supplierId,
  storefront,
  basketEnabled,
  currency,
}: {
  supplierId: number;
  storefront: SupplierStorefrontV2GetResponseType | undefined;
  basketEnabled: boolean;
  currency: SupplierGetResponseType["currency"];
}) => {
  const basketsQuery = useUnifiedCheckoutBasketList();

  const supplierBaskets = basketEnabled
    ? (basketsQuery.data?.filter((basket) =>
        basket.DraftOrders.some((draft) => draft.SupplierId === supplierId),
      ) ?? [])
    : [];

  const { orders, isLoading } = useUnifiedCheckoutDraftOrders(supplierBaskets);

  const supplierOrders = orders.filter(
    (order) => order.SupplierId === supplierId,
  );

  const allItems = supplierOrders.flatMap((order) => order.OrderItems ?? []);

  const expectedItemCount = supplierBaskets.reduce(
    (total, basket) =>
      total +
      basket.DraftOrders.filter(
        (draft) => draft.SupplierId === supplierId,
      ).reduce((count, draft) => count + draft.Items.length, 0),
    0,
  );

  let basket: OrderRequirementsBasket | undefined;
  // Missing or partially loaded product data must not become an empty basket.
  if (
    basketEnabled &&
    basketsQuery.data &&
    !isLoading &&
    allItems.length === expectedItemCount &&
    storefront
  ) {
    const items = allItems.filter((item) => !isPromotionalOrderItem(item));
    const subtotal = supplierOrders.reduce((total, order) => {
      const pricing = calculateUnifiedCheckoutOrderPricing({
        orderItems:
          order.OrderItems?.filter((item) => !isPromotionalOrderItem(item)) ??
          [],
        storefront,
        vatPercentage: 0,
      });
      return total + pricing.total;
    }, 0);

    basket = {
      items: items.map((item) => ({
        quantity: item.quantity,
        containerType: item.Product.containerType,
        volume: item.Product.volume,
        unit: item.Product.unit,
        packQuantity: item.Product.packQuantity,
      })),
      subtotal,
    };
  }

  return usePriceTierOrderRequirements({
    priceTierOrderRules: storefront?.OrderRules?.PriceTierOrderRules,
    minimumOrderPrice: storefront?.OrderRules?.minimumOrderPrice,
    caseSizeRuleList: storefront?.CaseSizeRules.list,
    currency,
    basket,
  });
};
